import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { api, fmtMoney, fmtPct } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Bot, Square, Copy, Brain, Activity, TrendingUp, TrendingDown, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { usePriceTicks, useSubscribePrices } from "../context/LiveStreamContext";
import { StalenessBadge } from "../components/StalenessBadge";

// Colour a bot's status pill the same way the /bots list does so users don't
// see two different palettes for the same concept.
const STATUS_COLOR = {
  running: "text-emerald-400 border-emerald-400/40",
  stopped: "text-muted-foreground border-border",
  completed: "text-blue-400 border-blue-400/40",
  paused: "text-amber-400 border-amber-400/40",
  halted: "text-red-400 border-red-400/40",
};

// Short human-readable event labels drawn from the structured event kinds
// emitted by /app/backend/bot_engine/events.py. Anything unknown falls back
// to the raw kind so the timeline never blanks out.
const EVENT_LABEL = {
  signal: "Signal",
  risk_ok: "Risk cleared",
  risk_block: "Risk blocked",
  order_submitted: "Order sent",
  order_filled: "Order filled",
  order_rejected: "Order rejected",
  halted: "Bot halted",
  reconciled: "Reconciled",
};

const EVENT_TONE = {
  risk_block: "text-red-400",
  order_rejected: "text-red-400",
  halted: "text-red-400",
  order_filled: "text-emerald-400",
  risk_ok: "text-emerald-400",
  signal: "text-blue-400",
  reconciled: "text-amber-400",
};

// Format a datetime string as "HH:MM · MMM D" for the event timeline / curve.
function fmtT(iso) {
  try {
    const d = new Date(iso);
    return `${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · ${d.toLocaleDateString([], { month: "short", day: "numeric" })}`;
  } catch { return iso || ""; }
}

export default function BotDashboard() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/bots/${botId}/dashboard`);
      setState(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load bot dashboard");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(); }, [botId]);
  // Silent refresh every 20s so the dashboard tracks the bot even between ticks
  useEffect(() => {
    const iv = setInterval(() => load(true), 20000);
    return () => clearInterval(iv);
  }, [botId]);

  // Subscribe to WS ticks for the bot's position symbols so P&L feels live
  const positions = state?.positions || [];
  useSubscribePrices(positions.map((p) => ({ symbol: p.symbol, asset_type: p.asset_type })));
  const live = usePriceTicks();

  // Overlay live ticks onto positions + top-line P&L so users see numbers move
  // without waiting for the 20s silent refresh.
  const positionsLive = useMemo(() => positions.map((p) => {
    const t = live[p.symbol];
    if (!t || t.p == null || !p.quantity) return p;
    const unrealized = (t.p - p.avg_cost) * p.quantity;
    return { ...p, current_price: t.p, market_value: t.p * p.quantity, unrealized_pnl: unrealized, _live: true };
  }), [positions, live]);

  const pnlLive = useMemo(() => {
    if (!state) return null;
    const un = positionsLive.reduce((s, p) => s + (p.unrealized_pnl || 0), 0);
    const total = (state.pnl.realized || 0) + un;
    const starting = state.pnl.starting_equity || 0;
    return {
      ...state.pnl,
      unrealized: un,
      total,
      equity_now: starting + total,
      return_pct: starting > 0 ? (total / starting) * 100 : 0,
    };
  }, [state, positionsLive]);

  const stopBot = async () => {
    try { await api.post(`/bots/${botId}/stop`); toast.success("Bot stopped"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't stop bot"); }
  };
  const cloneBot = async () => {
    try {
      const { data } = await api.post(`/bots/${botId}/clone`, { start: true });
      toast.success(`Cloned to "${data.name}"`, { action: { label: "Open", onClick: () => navigate(`/bots/${data.id}`) }});
    } catch (e) { toast.error(e?.response?.data?.detail || "Clone failed"); }
  };

  if (loading) {
    return <div className="text-muted-foreground p-6" data-testid="bot-dashboard-loading">Loading bot…</div>;
  }
  if (err) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center space-y-3" data-testid="bot-dashboard-error">
        <AlertTriangle className="w-8 h-8 mx-auto text-red-400" />
        <div className="font-display text-lg font-semibold">{err}</div>
        <Button onClick={() => navigate("/bots")} variant="outline" className="rounded-full">Back to bots</Button>
      </div>
    );
  }

  const bot = state.bot;
  const events = state.events || [];
  const openOrders = state.open_orders || [];
  const recentOrders = state.recent_orders || [];

  return (
    <div className="space-y-6" data-testid="bot-dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <button onClick={() => navigate("/bots")}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-2"
                  data-testid="bot-dashboard-back">
            <ArrowLeft className="w-4 h-4" /> Back to bots
          </button>
          <div className="flex items-center gap-3">
            <Bot className="w-6 h-6 text-emerald-400" />
            <h1 className="font-display text-3xl md:text-4xl font-bold" data-testid="bot-dashboard-name">{bot.name}</h1>
            <Badge variant="outline" className={`rounded-full uppercase text-[10px] ${STATUS_COLOR[bot.status] || ""}`}
                   data-testid="bot-dashboard-status">{bot.status}</Badge>
          </div>
          <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <span>Strategy · <span className="text-foreground font-medium">{bot.strategy}</span></span>
            <span>Route · <span className="text-foreground font-medium">{(bot.route || "sim").replace("_", " ")}</span></span>
            <span>Started · {fmtT(bot.started_at || bot.created_at)}</span>
            {bot.public && <Badge variant="secondary" className="rounded-full text-[10px]">public</Badge>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={cloneBot} className="rounded-full"
                  data-testid="bot-dashboard-clone">
            <Copy className="w-3.5 h-3.5 mr-1" /> Clone
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/bots/${bot.id}/postmortem`)}
                  className="rounded-full" data-testid="bot-dashboard-postmortem">
            <Brain className="w-3.5 h-3.5 mr-1" /> Postmortem
          </Button>
          {bot.status === "running" && (
            <Button variant="outline" size="sm" onClick={stopBot} className="rounded-full"
                    data-testid="bot-dashboard-stop">
              <Square className="w-3.5 h-3.5 mr-1" /> Stop
            </Button>
          )}
        </div>
      </div>

      {/* P&L strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="bot-dashboard-pnl-strip">
        <Card className="p-4 rounded-xl border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total P&amp;L</div>
          <div className={`font-mono-tab text-xl font-bold ${pnlLive.total >= 0 ? "gain" : "loss"}`}
               data-testid="pnl-total">
            {pnlLive.total >= 0 ? "+" : ""}{fmtMoney(pnlLive.total)}
          </div>
          <div className={`text-[11px] font-mono-tab ${pnlLive.return_pct >= 0 ? "gain" : "loss"}`}>
            {fmtPct(pnlLive.return_pct)}
          </div>
        </Card>
        <Card className="p-4 rounded-xl border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Realized</div>
          <div className={`font-mono-tab text-lg ${pnlLive.realized >= 0 ? "gain" : "loss"}`} data-testid="pnl-realized">
            {fmtMoney(pnlLive.realized)}
          </div>
        </Card>
        <Card className="p-4 rounded-xl border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Unrealized</div>
          <div className={`font-mono-tab text-lg ${pnlLive.unrealized >= 0 ? "gain" : "loss"}`} data-testid="pnl-unrealized">
            {fmtMoney(pnlLive.unrealized)}
          </div>
        </Card>
        <Card className="p-4 rounded-xl border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Equity now</div>
          <div className="font-mono-tab text-lg" data-testid="pnl-equity">{fmtMoney(pnlLive.equity_now)}</div>
          <div className="text-[11px] text-muted-foreground font-mono-tab">start {fmtMoney(pnlLive.starting_equity)}</div>
        </Card>
        <Card className="p-4 rounded-xl border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Trades</div>
          <div className="font-mono-tab text-lg" data-testid="pnl-trades">{state.trade_stats.total}</div>
          <div className="text-[11px] text-muted-foreground font-mono-tab">
            <span className="gain">{state.trade_stats.buys} buy</span> · <span className="loss">{state.trade_stats.sells} sell</span>
          </div>
        </Card>
      </div>

      {/* Equity curve */}
      <Card className="p-4 rounded-xl border-border" data-testid="bot-dashboard-equity-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Equity curve</div>
            <div className="font-display font-semibold flex items-center gap-2">
              Since launch
              <StalenessBadge symbols={
                positions.length
                  ? positions.map((p) => ({ symbol: p.symbol }))
                  : (state.bot?.symbols || state.bot?.assets || []).map((s) =>
                      ({ symbol: typeof s === "string" ? s : s.symbol }))
              } testId="bot-dashboard-freshness" />
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            hourly snapshot chain · signed
          </div>
        </div>
        <div className="h-56" data-testid="bot-dashboard-equity-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={state.equity_curve} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="t" tick={{ fontSize: 10 }} tickFormatter={(v) => {
                try { return new Date(v).toLocaleDateString([], { month: "short", day: "numeric" }); }
                catch { return v; }
              }} minTickGap={40} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]}
                     tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`}
                     stroke="hsl(var(--muted-foreground))" />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                labelFormatter={(v) => fmtT(v)}
                formatter={(v, name) => name === "equity" ? [fmtMoney(v), "Equity"] : [v, name]}
              />
              <ReferenceLine y={pnlLive.starting_equity} stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <Line type="monotone" dataKey="equity" stroke={pnlLive.total >= 0 ? "hsl(var(--gain))" : "hsl(var(--loss))"}
                    strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Positions */}
        <Card className="p-4 rounded-xl border-border" data-testid="bot-dashboard-positions">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" /> Open positions
            </div>
            <div className="text-[10px] uppercase text-muted-foreground">{positionsLive.filter((p) => Math.abs(p.quantity) > 0).length} live</div>
          </div>
          {positionsLive.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Bot hasn't taken any positions yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {positionsLive.map((p) => (
                <div key={p.symbol}
                     onClick={() => navigate(`/asset/${p.asset_type}/${p.symbol}`)}
                     className="grid grid-cols-6 gap-2 py-2 text-sm cursor-pointer hover:bg-secondary/40 rounded"
                     data-testid={`bot-position-${p.symbol}`}>
                  <div className="col-span-2 font-mono-tab font-medium">
                    {p.symbol}
                    {p._live && <span className="ml-2 text-[9px] uppercase tracking-widest text-emerald-400">·live</span>}
                    <div className="text-[10px] text-muted-foreground uppercase">{p.asset_type}</div>
                  </div>
                  <div className="col-span-1 text-right font-mono-tab text-xs">
                    <div>{p.quantity}</div>
                    <div className="text-[10px] text-muted-foreground">@ {fmtMoney(p.avg_cost, p.avg_cost > 100 ? 2 : 4)}</div>
                  </div>
                  <div className="col-span-1 text-right font-mono-tab text-xs">
                    <div>{fmtMoney(p.current_price, p.current_price > 100 ? 2 : 4)}</div>
                    <div className="text-[10px] text-muted-foreground">now</div>
                  </div>
                  <div className={`col-span-1 text-right font-mono-tab text-xs ${p.unrealized_pnl >= 0 ? "gain" : "loss"}`}>
                    <div>{p.unrealized_pnl >= 0 ? "+" : ""}{fmtMoney(p.unrealized_pnl)}</div>
                    <div className="text-[10px]">unr</div>
                  </div>
                  <div className={`col-span-1 text-right font-mono-tab text-xs ${p.realized_pnl >= 0 ? "gain" : "loss"}`}>
                    <div>{p.realized_pnl >= 0 ? "+" : ""}{fmtMoney(p.realized_pnl)}</div>
                    <div className="text-[10px]">real</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Open orders */}
        <Card className="p-4 rounded-xl border-border" data-testid="bot-dashboard-open-orders">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-400" /> Open orders
            </div>
            <div className="text-[10px] uppercase text-muted-foreground">{openOrders.length} working</div>
          </div>
          {openOrders.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No working orders. SIM orders fill instantly; IBKR-routed orders show up here while pending.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {openOrders.map((o) => (
                <div key={o.id || `${o.symbol}-${o.created_at}`}
                     className="grid grid-cols-6 gap-2 py-2 text-sm font-mono-tab"
                     data-testid={`bot-open-order-${o.id || o.symbol}`}>
                  <div className={`col-span-1 uppercase text-xs font-medium ${(o.side || "").toLowerCase() === "buy" ? "gain" : "loss"}`}>
                    {o.side}
                  </div>
                  <div className="col-span-2">{o.symbol}</div>
                  <div className="col-span-1 text-right text-xs">{o.quantity}</div>
                  <div className="col-span-1 text-right text-xs">{fmtMoney(o.price || 0)}</div>
                  <div className="col-span-1 text-right text-[10px] text-muted-foreground">{fmtT(o.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Event timeline + Recent orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-4 rounded-xl border-border" data-testid="bot-dashboard-events">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" /> Event timeline
            </div>
            <div className="text-[10px] uppercase text-muted-foreground">last {events.length}</div>
          </div>
          {events.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No events yet. Signals, risk checks, and order lifecycle will appear here as the bot runs.
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-0 divide-y divide-border" data-testid="bot-dashboard-events-list">
              {events.map((e, i) => (
                <div key={e.id || i} className="py-2 text-xs" data-testid={`bot-event-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`uppercase tracking-widest text-[10px] font-semibold ${EVENT_TONE[e.kind] || ""}`}>
                      {EVENT_LABEL[e.kind] || e.kind}
                    </span>
                    <span className="font-mono-tab text-muted-foreground text-[10px]">{fmtT(e.t || e.created_at)}</span>
                  </div>
                  {(e.symbol || e.message) && (
                    <div className="text-muted-foreground mt-0.5">
                      {e.symbol && <span className="font-mono-tab font-medium text-foreground">{e.symbol} </span>}
                      {e.message || e.reason || ""}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4 rounded-xl border-border" data-testid="bot-dashboard-recent-orders">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-emerald-400" /> Recent orders
            </div>
            <div className="text-[10px] uppercase text-muted-foreground">last {recentOrders.length}</div>
          </div>
          {recentOrders.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No fills yet.</div>
          ) : (
            <div className="max-h-96 overflow-y-auto divide-y divide-border">
              {recentOrders.map((o) => (
                <div key={o.id || `${o.symbol}-${o.created_at}`}
                     className="grid grid-cols-6 gap-2 py-2 text-sm font-mono-tab"
                     data-testid={`bot-recent-order-${o.id || o.symbol}`}>
                  <div className={`col-span-1 uppercase text-xs font-medium ${(o.side || "").toLowerCase() === "buy" ? "gain" : "loss"}`}>
                    {o.side}
                  </div>
                  <div className="col-span-2">{o.symbol}</div>
                  <div className="col-span-1 text-right text-xs">{o.quantity}</div>
                  <div className="col-span-1 text-right text-xs">{fmtMoney(o.price || 0)}</div>
                  <div className="col-span-1 text-right text-[10px] text-muted-foreground">{fmtT(o.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
